import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Lock, Shield, Building2 } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyMFA } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMFA, setShowMFA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [debugCode, setDebugCode] = useState('');

  const SMJ_LOGO_URL = "https://customer-assets.emergentagent.com/job_funds-portal/artifacts/xi0cc8ut_Clean%20Logo%20with%20Words.png";

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setShowMFA(true);
        setDebugCode(result.debugCode || '');
      } else if (result.success) {
        if (result.user.role === 'client') {
          navigate('/portal');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMFA = async (e) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length !== 6) {
      setError('Please enter 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const user = await verifyMFA(email, mfaCode);
      if (user.role === 'client') {
        navigate('/portal');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      data-testid="login-page"
      style={{ 
        minHeight: '100vh', 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0A1520',
        padding: '40px 20px',
      }}
    >
      {/* Logo + Portal Title as one tight unit */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img 
          src={SMJ_LOGO_URL} 
          alt="Steele Martin Jones - CPAs and Business Advisors" 
          style={{ 
            height: 'auto', 
            width: 300,
            maxWidth: '85%',
            display: 'block',
            margin: '0 auto',
          }} 
        />
        <div style={{ 
          fontSize: 13, 
          color: '#0EA5E9', 
          letterSpacing: '0.3em', 
          textTransform: 'uppercase',
          fontWeight: 600,
          marginTop: -18,
        }}>
          Client Financial Portal
        </div>
      </div>

      {/* Login Card */}
      <div 
        style={{ 
          width: '100%',
          maxWidth: 400,
          background: '#1E293B',
          borderRadius: 12,
          padding: '32px 28px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
        }}
      >
        <h2 
          style={{ 
            fontSize: 20, 
            fontWeight: 500, 
            color: '#F1F5F9', 
            margin: '0 0 4px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {showMFA ? 'Enter verification code' : 'Sign in to your portal'}
        </h2>
        <p style={{ color: '#94A3B8', fontSize: 13, margin: '0 0 22px' }}>
          {showMFA 
            ? 'A 6-digit code has been sent to your email.'
            : 'Your financial data is encrypted and protected.'}
        </p>

        {error && (
          <div 
            data-testid="login-error"
            style={{ 
              padding: '10px 14px', 
              background: 'rgba(239, 68, 68, 0.15)', 
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 6, 
              color: '#F87171', 
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {debugCode && showMFA && (
          <div 
            style={{ 
              padding: '10px 14px', 
              background: 'rgba(14, 165, 233, 0.15)', 
              border: '1px solid rgba(14, 165, 233, 0.3)',
              borderRadius: 6, 
              color: '#38BDF8', 
              fontSize: 11,
              marginBottom: 16,
            }}
          >
            <strong>Demo Mode:</strong> Your MFA code is <code style={{ fontWeight: 700, background: 'rgba(0,0,0,0.3)', padding: '2px 5px', borderRadius: 3 }}>{debugCode}</code>
          </div>
        )}

        {!showMFA ? (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 16 }}>
              <label 
                style={{ 
                  display: 'block', 
                  fontSize: 10, 
                  fontWeight: 600, 
                  color: '#94A3B8', 
                  marginBottom: 6,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Email Address
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 6,
                  border: '1px solid #334155',
                  background: '#0F172A',
                  fontSize: 14,
                  color: '#F1F5F9',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                onBlur={(e) => e.target.style.borderColor = '#334155'}
              />
            </div>

            <div style={{ marginBottom: 22 }}>
              <label 
                style={{ 
                  display: 'block', 
                  fontSize: 10, 
                  fontWeight: 600, 
                  color: '#94A3B8', 
                  marginBottom: 6,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Password
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 6,
                  border: '1px solid #334155',
                  background: '#0F172A',
                  fontSize: 14,
                  color: '#F1F5F9',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                onBlur={(e) => e.target.style.borderColor = '#334155'}
              />
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '13px 20px',
                borderRadius: 6,
                background: loading ? '#475569' : 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? 'Signing in...' : 'Continue →'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleMFA}>
            <div style={{ marginBottom: 22 }}>
              <label 
                style={{ 
                  display: 'block', 
                  fontSize: 10, 
                  fontWeight: 600, 
                  color: '#94A3B8', 
                  marginBottom: 6,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                6-Digit Code
              </label>
              <input
                data-testid="mfa-code-input"
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 6,
                  border: '1px solid #334155',
                  background: '#0F172A',
                  fontSize: 24,
                  fontWeight: 600,
                  textAlign: 'center',
                  letterSpacing: '0.35em',
                  color: '#F1F5F9',
                  fontFamily: 'monospace',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#0EA5E9'}
                onBlur={(e) => e.target.style.borderColor = '#334155'}
              />
            </div>

            <button
              data-testid="mfa-submit-btn"
              type="submit"
              disabled={loading || mfaCode.length !== 6}
              style={{
                width: '100%',
                padding: '13px 20px',
                borderRadius: 6,
                background: loading || mfaCode.length !== 6 ? '#475569' : 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                border: 'none',
                cursor: loading || mfaCode.length !== 6 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? 'Verifying...' : 'Verify & Continue →'}
            </button>

            <button
              type="button"
              onClick={() => { setShowMFA(false); setMfaCode(''); setError(''); }}
              style={{
                width: '100%',
                padding: '10px',
                marginTop: 10,
                background: 'transparent',
                color: '#64748B',
                fontSize: 12,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              ← Back to login
            </button>
          </form>
        )}

        {/* Security badges */}
        <div style={{ 
          marginTop: 20, 
          paddingTop: 16, 
          borderTop: '1px solid #334155',
          display: 'flex',
          justifyContent: 'center',
          gap: 18,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748B', fontSize: 10 }}>
            <Lock size={10} color="#F59E0B" />
            <span>256-bit SSL</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748B', fontSize: 10 }}>
            <Shield size={10} />
            <span>MFA Protected</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748B', fontSize: 10 }}>
            <Building2 size={10} />
            <span>Bank-level Security</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <p style={{ color: '#475569', fontSize: 11, margin: '0 0 2px' }}>
          Prepared and managed by Steele Martin Jones & Company, PLC
        </p>
        <p style={{ color: '#475569', fontSize: 11, margin: 0 }}>
          Questions? Call (731) 668-9183
        </p>
      </div>

      {/* Demo credentials — DEV ONLY. Hidden in any production build
          (NODE_ENV=production) and also hidden whenever the SSO portal
          URL is configured. */}
      {process.env.NODE_ENV !== 'production' && !process.env.REACT_APP_PORTAL_URL && (
        <div style={{ 
          marginTop: 16, 
          padding: '8px 16px', 
          background: 'rgba(30, 41, 59, 0.5)', 
          borderRadius: 6,
          textAlign: 'center',
        }}>
          <p style={{ color: '#64748B', fontSize: 9, margin: '0 0 3px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Demo Credentials
          </p>
          <p style={{ color: '#94A3B8', fontSize: 10, margin: '0 0 1px' }}>
            Staff: <code style={{ color: '#E2E8F0' }}>staff@steelemartin.com</code> / <code style={{ color: '#E2E8F0' }}>staff123</code>
          </p>
          <p style={{ color: '#94A3B8', fontSize: 10, margin: 0 }}>
            Client: <code style={{ color: '#E2E8F0' }}>grant@gtfarm.com</code> / <code style={{ color: '#E2E8F0' }}>client123</code>
          </p>
        </div>
      )}
    </div>
  );
}
